import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'src/lib/routing';
import { useConnectedClient } from 'src/context/ConnectedClientContext';
import { useAutoSwitchTeam } from 'src/hooks/useAutoSwitchTeam';

import { ErrorPage } from 'components/ErrorPage/ErrorPage';

import type { ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

export const TeamContextResolver = ({ children }: Props) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { details, isLoading } = useConnectedClient();

  const teamIdFromUrl = searchParams.get('teamId');
  const currentTeamId = details?.team?.id;
  const userTeams = details?.Teams;

  // No teamId in URL — render children immediately
  if (!teamIdFromUrl) {
    return <>{children}</>;
  }

  // Already on the correct team — render children
  if (currentTeamId === teamIdFromUrl) {
    return <>{children}</>;
  }

  // Still loading user details — don't make access decisions yet
  if (isLoading) {
    return <AskableIconLoading isFullScreen />;
  }

  // User belongs to the target team but is on the wrong one — auto-switch
  const belongsToTargetTeam = userTeams?.some(team => team?._id === teamIdFromUrl) ?? false;

  if (belongsToTargetTeam) {
    return <TeamSwitching teamIdFromUrl={teamIdFromUrl}>{children}</TeamSwitching>;
  }

  // User does NOT belong to the target team — access denied
  return (
    <ErrorPage>
      <h1 className="text-xl font-semibold">{t('global.teamContextResolver.accessDeniedTitle')}</h1>
      <p>{t('global.teamContextResolver.accessDeniedDescription')}</p>
    </ErrorPage>
  );
};

/**
 * Separate component to call useAutoSwitchTeam only when we know we need to switch.
 * This avoids calling the hook conditionally in the parent.
 */
const TeamSwitching = ({ teamIdFromUrl, children }: { teamIdFromUrl: string; children: ReactNode }) => {
  const { isSwitchingTeam } = useAutoSwitchTeam({
    targetTeamId: teamIdFromUrl,
    redirectUrl: (() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('teamId');
      return url.toString();
    })(),
  });

  if (isSwitchingTeam) {
    return <AskableIconLoading isFullScreen />;
  }

  return <>{children}</>;
};
